// ============================================================================
// API helper — fetchuje data z Netlify Functions s automatickým auth headerem
// ============================================================================

import { useEffect, useState } from "react";

// Když je cookie a Bearer token oba neplatné nebo expirované, backend vrátí 401.
// Auto-handler: smaž session info v localStorage a přesměruj na /login.
// Endpointy které jsou bez auth (auth-verify, auth-logout) tuto logiku obejdou.
const NO_AUTH_PATHS = ["/api/auth-verify", "/api/auth-logout"];
function handleAuthFailure(input) {
  const url = typeof input === "string" ? input : (input && input.url) || "";
  if (NO_AUTH_PATHS.some((p) => url.includes(p))) return; // login/logout sám
  try {
    localStorage.removeItem("fw40_session");
    localStorage.removeItem("fw40_id_token"); // uklid legacy tokenu
  } catch { /* ignore */ }
  // Hard redirect ať React přepne na /login
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

// Low-level wrapper kolem window.fetch.
// Primární auth: httpOnly cookie fw40_session — browser ji posílá automaticky když má fetch
// credentials: "include". Cookie není přístupná z JavaScriptu (XSS-resistant).
// Legacy Bearer fallback (localStorage.fw40_id_token) ODSTRANEN — audit 20.7.2026:
// obchazel PG stav uzivatele. Stary user se proste znovu prihlasi.
// 401 auto-handler: smaže session a přesměruje na /login (cookie expirovala / user revoked).
// Vrací nativní Response — drop-in replacement za fetch().
export async function apiFetch(input, init = {}) {
  const headers = { ...(init.headers || {}) };
  if (init.body && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  // credentials: "include" → browser posílá cookies (i httpOnly fw40_session) i přes cross-origin.
  const response = await fetch(input, { credentials: "include", ...init, headers });
  if (response.status === 401) handleAuthFailure(input);
  return response;
}

/**
 * Helper pro fetch dat z backend endpointu — vrací parsed JSON, throws na non-2xx.
 * Auth: httpOnly cookie fw40_session (posílá apiFetch přes credentials: include).
 * @param {string} endpoint - např. "/api/data/overview"
 * @param {RequestInit} [options] - method, body, headers atd.
 * @returns {Promise<any>}
 */
export async function fetchApi(endpoint, options = {}) {
  const response = await apiFetch(endpoint, options);
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * React hook pro fetchování dat z API.
 * Okamžitě zobrazí poslední uložená data z localStorage (pokud existují),
 * zatímco na pozadí stahuje čerstvá data z API.
 * @param {string} endpoint
 * @returns {{ data, loading, error, refetch }}
 */
// Bezpečné uložení API cache do localStorage. Když je kvóta plná, vyházej STARÉ
// api_cache_ položky (uvolní místo i pro session/náhled — jinak se náhled „zobrazit
// jako" tiše rozbije, protože setViewAsRole neuloží). Obří odpovědi vůbec nekešuj.
function cacheSetSafe(key, value) {
  let val;
  try { val = JSON.stringify(value); } catch { return; }
  if (val.length > 512 * 1024) return; // >512 KB necachovat (bloatuje localStorage)
  try { localStorage.setItem(key, val); return; } catch (_) { /* nejspíš QuotaExceeded */ }
  try {
    const stare = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("api_cache_") && k !== key) stare.push(k);
    }
    for (const k of stare) localStorage.removeItem(k);
    localStorage.setItem(key, val);
  } catch (_) { /* i tak nevešlo — necache, ale kvóta je teď uvolněná */ }
}

// options.bezKese = true → nepoužije se uložená odpověď z minula. Hodí se tam,
// kde by zastaralá data zmátla: 24.8.2026 v rozvrhu vzoru po obnovení stránky
// na okamžik probliknul starý stav slotu, než dorazila čerstvá data.
export function useApiData(endpoint, options = {}) {
  const bezKese = options.bezKese === true;
  // Helper pro load z localStorage podle klíče
  const getCachedFor = (ep) => {
    if (bezKese) return null;
    try {
      const cached = localStorage.getItem(`api_cache_${ep}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  // Čas posledního úspěšného načtení (8.9.2026). Drží se i v localStorage, aby po
  // obnovení stránky šlo říct "ukazuju stav z 10:31" a ne čas otevření aplikace.
  const getCachedTimeFor = (ep) => {
    if (bezKese) return null;
    try {
      const t = parseInt(localStorage.getItem(`api_cache_cas_${ep}`), 10);
      return Number.isFinite(t) ? t : null;
    } catch {
      return null;
    }
  };

  const initialCached = endpoint ? getCachedFor(endpoint) : null;
  const [data, setData] = useState(initialCached);
  const [loading, setLoading] = useState(!!endpoint && !initialCached);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [nactenoV, setNactenoV] = useState(endpoint ? getCachedTimeFor(endpoint) : null);

  const load = async (ep, force = false) => {
    if (!ep) return;
    const startTime = Date.now();
    setRefreshing(true); // vždy ukaž že načítám (i když mám cache)
    setError(null);
    try {
      const sep = ep.includes("?") ? "&" : "?";
      const url = force ? `${ep}${sep}force=1` : ep;
      // 8.9.2026: dvě opakování při výpadku spojení. Na telefonu se běžně stane,
      // že se požadavek nedokončí (přepnutí sítě, uspaná aplikace, pomalý cold
      // start funkce) a Safari vrátí "Load failed". Dřív to rovnou spadlo do
      // chybové hlášky, i když druhý pokus o vteřinu později projde.
      // Opakuje se JEN síťová chyba, ne odpověď serveru (401, 500) — ta by se
      // opakováním stejně nespravila.
      let result, posledniChyba;
      for (let pokus = 0; pokus < 3; pokus++) {
        try {
          result = await fetchApi(url);
          posledniChyba = null;
          break;
        } catch (e) {
          posledniChyba = e;
          const sitovaChyba = e instanceof TypeError || /load failed|failed to fetch|network/i.test(e?.message || "");
          if (!sitovaChyba || pokus === 2) break;
          await new Promise((r) => setTimeout(r, pokus === 0 ? 700 : 1800));
        }
      }
      if (posledniChyba) throw posledniChyba;
      setData(result);
      cacheSetSafe(`api_cache_${ep}`, result);
      const ted = Date.now();
      setNactenoV(ted);
      try { localStorage.setItem(`api_cache_cas_${ep}`, String(ted)); } catch { /* plná kvóta, čas není kritický */ }
    } catch (e) {
      setError(e.message);
    } finally {
      if (force) {
        const elapsed = Date.now() - startTime;
        if (elapsed < 800) await new Promise((r) => setTimeout(r, 800 - elapsed));
      }
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Při změně endpointu: okamžitě resetuj data + show loader (s fallbackem na localStorage cache)
  useEffect(() => {
    if (!endpoint) {
      setData(null);
      setLoading(false);
      return;
    }
    const cached = getCachedFor(endpoint);
    setData(cached);
    setNactenoV(getCachedTimeFor(endpoint));
    setLoading(!cached);
    setError(null);
    load(endpoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  return {
    data,
    setData, // pro optimistic updates — komponenta může lokálně předbíhat backend
    nactenoV, // timestamp posledního úspěšného načtení (i z minulé návštěvy)
    loading,
    error,
    refetch: () => load(endpoint, true),
    refreshing,
  };
}
